import React, { useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppContext } from '../../App';
import { useIsLabViewer } from '../../hooks/useIsLabViewer';
import {
  getProjectDetails,
  getProjectProducts,
  getProjectQuotes,
  getProjectOrders,
} from '../../clients/project_client';
import { getOrderContactSettings } from '../../clients/order_request_client';
import { formatQuoteBudgetDisplay } from '../../utils/quoteFundingDisplay';
import { Spinner, Nav, Tab, Button, Badge } from 'react-bootstrap';
import { formatDateStr, getProjectStatusLabel, getProjectProductStatusLabel, formatProgressPercentage, formatOwnerDisplay, showToast } from '../../config_and_helpers/helpers';
import InventoryProgressBar from '../../components/Generic/InventoryProgressBar';
import ProjectFormModal from '../../components/Project/ProjectFormModal';
import ProjectProductStatusModal from '../../components/Project/ProjectProductStatusModal';
import ProjectProductsQuotePanel from '../../components/Project/ProjectProductsQuotePanel';
import QuoteTable from '../../components/Quote/QuoteTable';
import OrderTable from '../../components/Order/OrderTable';
import QuoteModal from '../../components/Quote/QuoteModal';
import OrderModal from '../../components/Order/OrderModal';
import GlobalOrderContactModal from '../../components/OrderRequest/GlobalOrderContactModal';
import Table from 'react-bootstrap/Table';
import { logError, logInfo } from '../../config_and_helpers/logger';
import GlassWorkspaceShell from '../../components/Glass/GlassWorkspaceShell';
import './ProjectPageStyle.css';
import {
  buildSupplierGroupsForProjectQuotes,
  isAdHocProjectProduct,
  isInventoryProjectProduct,
} from '../../utils/projectQuoteRequest';

/**
 * ProjectDetailPage displays detailed information about a project including
 * products, quotes, and orders in separate tabs.
 */
const ProjectDetailPage = () => {
  const { token } = useContext(AppContext);
  const isViewer = useIsLabViewer();
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [showGlobalContactModal, setShowGlobalContactModal] = useState(false);
  const [globalContactSettings, setGlobalContactSettings] = useState(null);
  const [isLoadingGlobalContact, setIsLoadingGlobalContact] = useState(false);
  const [globalContactLoadError, setGlobalContactLoadError] = useState('');

  const supplierGroups = useMemo(
    () => buildSupplierGroupsForProjectQuotes(products),
    [products]
  );

  /** Prefer embedded `quotes` on project retrieve; fall back to get_quotes endpoint. */
  const reloadProjectQuotes = useCallback(async () => {
    if (!projectId) return;

    setLoadingQuotes(true);
    const projectResponse = await getProjectDetails(token, projectId, setProject);
    if (projectResponse?.success) {
      const projectData = projectResponse.projectData;
      setProject(projectData);
      if (Array.isArray(projectData?.quotes)) {
        logInfo('ProjectDetailPage', 'Using embedded project.quotes', {
          projectId,
          count: projectData.quotes.length,
        });
        setQuotes(projectData.quotes);
        setLoadingQuotes(false);
        return;
      }
    }

    const quotesResponse = await getProjectQuotes(token, projectId);
    if (quotesResponse?.success) {
      setQuotes(quotesResponse.quotes || []);
    }
    setLoadingQuotes(false);
  }, [token, projectId]);

  const hasGlobalContactSettings =
    !!globalContactSettings?.contact_name && !!globalContactSettings?.contact_email;

  let globalContactDisabledReason = '';
  if (isLoadingGlobalContact) {
    globalContactDisabledReason = 'Loading lab contact settings...';
  } else if (globalContactLoadError) {
    globalContactDisabledReason =
      'Quote requests are disabled because lab contact settings could not be loaded.';
  } else if (!hasGlobalContactSettings) {
    globalContactDisabledReason =
      'Quote requests are disabled until a lab contact is configured.';
  }

  // Function to refresh project data after quote/order operations
  const refreshProjectData = useCallback(async () => {
    if (!projectId) return;
    
    // Store previous status for comparison
    const previousStatus = project?.status;
    let updatedProject = null;

    // Refresh project details
    const projectResponse = await getProjectDetails(token, projectId, setProject);
    if (projectResponse && projectResponse.success) {
      updatedProject = projectResponse.projectData;
      setProject(updatedProject);
      
      // Show notification if project status changed
      if (previousStatus && previousStatus !== updatedProject.status) {
        showToast(
          `Project status updated: ${getProjectStatusLabel(previousStatus)} → ${getProjectStatusLabel(updatedProject.status)}`,
          'success',
          'top-right',
          4000
        );
      }
    }
    
    // Always sync project lines after quote/order flows (ad-hoc promotion, status sync)
    if (activeTab === 'products') {
      setLoadingProducts(true);
    }
    let syncedLineCount = 0;
    let productsSource = 'none';
    if (Array.isArray(updatedProject?.project_products)) {
      syncedLineCount = updatedProject.project_products.length;
      productsSource = 'embedded';
      setProducts(updatedProject.project_products);
      if (activeTab === 'products') {
        setLoadingProducts(false);
      }
    } else {
      const productsResponse = await getProjectProducts(token, projectId);
      if (productsResponse && productsResponse.success) {
        const lines = productsResponse.products || [];
        syncedLineCount = lines.length;
        productsSource = 'get_products';
        setProducts(lines);
      }
      if (activeTab === 'products') {
        setLoadingProducts(false);
      }
    }
    logInfo('ProjectDetailPage', 'refreshProjectData synced project_products', {
      projectId,
      productsSource,
      lineCount: syncedLineCount,
      activeTab,
    });
    
    // Refresh quotes if quotes tab is active
    if (activeTab === 'quotes') {
      if (Array.isArray(updatedProject?.quotes)) {
        setQuotes(updatedProject.quotes);
      } else {
        await reloadProjectQuotes();
      }
    }
    
    // Refresh orders if orders tab is active
    if (activeTab === 'orders') {
      setLoadingOrders(true);
      const ordersResponse = await getProjectOrders(token, projectId);
      if (ordersResponse && ordersResponse.success) {
        setOrders(ordersResponse.orders || []);
      }
      setLoadingOrders(false);
    }
  }, [token, projectId, activeTab, project?.status, reloadProjectQuotes]);

  // Fetch project details
  useEffect(() => {
    const fetchProject = async () => {
      setLoading(true);
      const response = await getProjectDetails(token, projectId, setProject);
      if (response && response.success) {
        const projectData = response.projectData;
        setProject(projectData);
        if (Array.isArray(projectData?.quotes)) {
          setQuotes(projectData.quotes);
        }
      } else {
        showToast('Failed to load project details', 'error', 'top-right', 3000);
        navigate('/projects');
      }
      setLoading(false);
    };

    if (projectId) {
      fetchProject();
    }
  }, [token, projectId, navigate]);

  useEffect(() => {
    if (!token) return;
    const loadContactSettings = async () => {
      setIsLoadingGlobalContact(true);
      setGlobalContactLoadError('');
      const response = await getOrderContactSettings(token);
      setIsLoadingGlobalContact(false);
      if (response?.success) {
        setGlobalContactSettings(response.data);
      } else {
        setGlobalContactSettings(null);
        setGlobalContactLoadError(
          response?.messages?.[0] ||
            'Lab contact settings could not be loaded.'
        );
      }
    };
    loadContactSettings();
  }, [token]);

  // Fetch products when products tab is active
  useEffect(() => {
      if (activeTab === 'products' && projectId && !products.length) {
      setLoadingProducts(true);
      getProjectProducts(token, projectId)
        .then(response => {
          if (response && response.success) {
            setProducts(response.products || []);
          }
          setLoadingProducts(false);
        })
        .catch(error => {
          logError('ProjectDetailPage', 'getProjectProducts failed', error);
          setLoadingProducts(false);
        });
    }
  }, [activeTab, projectId, token, products.length]);

  // Fetch quotes when quotes tab is active (embedded on project detail, or get_quotes fallback)
  useEffect(() => {
    if (activeTab !== 'quotes' || !projectId) return;

    if (Array.isArray(project?.quotes)) {
      setQuotes(project.quotes);
      return;
    }

    if (quotes.length > 0) return;

    reloadProjectQuotes().catch(error => {
      logError('ProjectDetailPage', 'reloadProjectQuotes failed', error);
    });
  }, [activeTab, projectId, project?.quotes, quotes.length, reloadProjectQuotes]);

  // Fetch orders when orders tab is active
  useEffect(() => {
    if (activeTab === 'orders' && projectId && !orders.length) {
      setLoadingOrders(true);
      getProjectOrders(token, projectId)
        .then(response => {
          if (response && response.success) {
            setOrders(response.orders || []);
          }
          setLoadingOrders(false);
        })
        .catch(error => {
          logError('ProjectDetailPage', 'getProjectOrders failed', error);
          setLoadingOrders(false);
        });
    }
  }, [activeTab, projectId, token, orders.length]);

  const refreshProject = async () => {
    const response = await getProjectDetails(token, projectId, setProject);
    if (response && response.success) {
      const projectData = response.projectData;
      setProject(projectData);
      if (Array.isArray(projectData?.quotes)) {
        setQuotes(projectData.quotes);
      }
    }
  };

  const handleProductStatusUpdate = () => {
    // Refresh products and project details
    setProducts([]);
    refreshProject();
  };

  const getStatusBadgeVariant = status => {
    const variantMap = {
      PLANNING: 'secondary',
      QUOTING: 'info',
      ORDERING: 'warning',
      RECEIVING: 'primary',
      COMPLETED: 'success',
      ON_HOLD: 'danger',
    };
    return variantMap[status] || 'secondary';
  };

  const getProductStatusBadgeVariant = status => {
    const variantMap = {
      NEEDS_QUOTE: 'warning',
      QUOTED: 'info',
      ORDERED: 'primary',
      PARTIAL: 'warning',
      RECEIVED: 'success',
    };
    return variantMap[status] || 'secondary';
  };

  if (loading) {
    return (
      <GlassWorkspaceShell theme="yellow" wide>
        <div className="text-center py-5">
          <Spinner animation="border" role="status" />
          <div className="mt-2">Loading project details...</div>
        </div>
      </GlassWorkspaceShell>
    );
  }

  if (!project) {
    return (
      <GlassWorkspaceShell theme="yellow" wide>
        <div className="glass-empty">
          <h5>Project not found</h5>
          <Button variant="outline-secondary" onClick={() => navigate('/projects')}>
            Back to Projects
          </Button>
        </div>
      </GlassWorkspaceShell>
    );
  }

  return (
    <GlassWorkspaceShell theme="yellow" wide>
      <div className="glass-panel project-detail-header">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
          <div>
            <h2 className="project-detail-header__title">{project.name}</h2>
            <div className="project-detail-meta">
              <Badge bg={getStatusBadgeVariant(project.status)}>
                {getProjectStatusLabel(project.status)}
              </Badge>
              {(project.budget_line || project.budget) && (
                <span>Budget: {formatQuoteBudgetDisplay(project)}</span>
              )}
              {project.owner && (
                <span>Owner: {formatOwnerDisplay(project.owner)}</span>
              )}
            </div>
          </div>
          <ProjectFormModal
            projectObj={project}
            onSuccessfulSubmit={() => {
              refreshProject();
              navigate('/projects');
            }}
          />
        </div>
        {project.description ? (
          <p className="project-detail-description mb-0 mt-3">{project.description}</p>
        ) : null}
      </div>

      <div className="project-detail-stats">
        <div className="project-detail-stat">
          <p className="project-detail-stat__label">Progress</p>
          <InventoryProgressBar
            currentValue={project.completed_products || 0}
            totalValue={project.total_products || 0}
          />
          <div className="mt-2">
            <strong>{formatProgressPercentage(project.progress_percentage)}</strong>
            <span className="text-muted ms-2">
              ({project.completed_products || 0} / {project.total_products || 0} products)
            </span>
          </div>
        </div>
        <div className="project-detail-stat">
          <p className="project-detail-stat__label">Expected Completion</p>
          <div>
            {project.expected_completion_date
              ? formatDateStr(project.expected_completion_date)
              : 'Not set'}
          </div>
        </div>
        <div className="project-detail-stat">
          <p className="project-detail-stat__label">Last Updated</p>
          <div>
            {project.last_updated
              ? formatDateStr(project.last_updated)
              : formatDateStr(project.created_date)}
          </div>
        </div>
      </div>

      <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
        <div className="project-detail-tabs">
          <Nav variant="tabs">
            <Nav.Item>
              <Nav.Link eventKey="products">Products</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="quotes">Quotes</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="orders">Orders</Nav.Link>
            </Nav.Item>
          </Nav>
        </div>

        <Tab.Content className="project-detail-tab-pane">
          <Tab.Pane eventKey="products">
            {loadingProducts ? (
              <div className="text-center py-5">
                <Spinner animation="border" role="status" />
              </div>
            ) : products.length === 0 ? (
              <div className="glass-empty">
                <p className="mb-0">No products added to this project yet.</p>
              </div>
            ) : (
              <div>
                <div className="project-detail-actions">
                  {!isViewer ? (
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setShowGlobalContactModal(true)}
                  >
                    {hasGlobalContactSettings ? 'Lab contact set' : 'Set lab contact'}
                  </Button>
                  ) : null}
                </div>
                {supplierGroups.map(group => {
                  const groupProducts = [
                    ...group.inventoryProducts,
                    ...group.adHocProducts,
                  ];
                  return (
                    <div key={group.supplierKey} className="project-supplier-group">
                      <div className="project-supplier-group__header">
                        <h5 className="project-supplier-group__title">
                          <Badge bg="secondary" className="me-2">
                            {group.supplierName}
                          </Badge>
                          <span className="text-muted fw-normal">
                            ({groupProducts.length} product
                            {groupProducts.length !== 1 ? 's' : ''}
                            {group.inventoryProducts.length > 0 ||
                            group.adHocProducts.length > 0 ? (
                              <>
                                {' '}
                                · {group.inventoryProducts.length} catalog
                                {group.adHocProducts.length > 0
                                  ? ` · ${group.adHocProducts.length} custom`
                                  : ''}
                              </>
                            ) : null}
                            )
                          </span>
                        </h5>
                        <ProjectProductsQuotePanel
                          projectId={projectId}
                          supplierName={group.supplierName}
                          inventoryProducts={group.inventoryProducts}
                          adHocProducts={group.adHocProducts}
                          hasGlobalContact={hasGlobalContactSettings}
                          contactDisabledReason={globalContactDisabledReason}
                          onQuotesSent={refreshProjectData}
                        />
                      </div>
                      <div className="glass-table-wrap">
                      <Table striped bordered hover className="mb-0">
                        <thead>
                          <tr className="text-center">
                            <th>Product</th>
                            <th>CAT#</th>
                            <th>Price</th>
                            <th>Quantity Needed</th>
                            <th>Status</th>
                            <th>Notes</th>
                            <th>Last Status Update</th>
                            {!isViewer ? <th>Actions</th> : null}
                          </tr>
                        </thead>
                        <tbody>
                          {groupProducts.map(projectProduct => (
                            <tr
                              key={projectProduct.id}
                              className="text-center align-middle"
                            >
                              <td>
                                <div>
                                  {projectProduct.display_name ||
                                    projectProduct.product?.name ||
                                    projectProduct.line_name ||
                                    '—'}
                                </div>
                                {isAdHocProjectProduct(projectProduct) ? (
                                  <Badge bg="info" className="mt-1">
                                    Not in inventory
                                  </Badge>
                                ) : isInventoryProjectProduct(projectProduct) ? (
                                  <Badge bg="success" className="mt-1">
                                    In catalog
                                  </Badge>
                                ) : null}
                              </td>
                              <td>
                                {projectProduct.display_cat_num ||
                                  projectProduct.product?.cat_num ||
                                  '—'}
                              </td>
                              <td>
                                {projectProduct.line_price != null &&
                                projectProduct.line_price !== ''
                                  ? `${projectProduct.line_price}${
                                      projectProduct.line_currency
                                        ? ` ${projectProduct.line_currency}`
                                        : ''
                                    }`
                                  : projectProduct.product?.price != null
                                    ? `${projectProduct.product.price}${
                                        projectProduct.product.currency
                                          ? ` ${projectProduct.product.currency}`
                                          : ''
                                      }`
                                    : '—'}
                              </td>
                              <td>{projectProduct.quantity_needed}</td>
                              <td>
                                <Badge bg={getProductStatusBadgeVariant(projectProduct.status)}>
                                  {getProjectProductStatusLabel(projectProduct.status)}
                                </Badge>
                              </td>
                              <td>{projectProduct.notes || '-'}</td>
                              <td>
                                {projectProduct.last_status_update
                                  ? formatDateStr(projectProduct.last_status_update)
                                  : '-'}
                              </td>
                              {!isViewer ? (
                              <td>
                                <ProjectProductStatusModal
                                  projectId={projectId}
                                  productId={projectProduct.product_id}
                                  projectProductId={projectProduct.id}
                                  currentStatus={projectProduct.status}
                                  onStatusUpdate={handleProductStatusUpdate}
                                />
                              </td>
                              ) : null}
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Tab.Pane>

          <Tab.Pane eventKey="quotes">
            {loadingQuotes ? (
              <div className="text-center py-5">
                <Spinner animation="border" role="status" />
              </div>
            ) : quotes.length === 0 ? (
              <div className="glass-empty">
                <p className="mb-0">No quotes associated with this project.</p>
              </div>
            ) : (
              <>
                {!isViewer ? (
                <div className="glass-toolbar mb-3">
                  <QuoteModal
                    onSuccessfulSubmit={() => {
                      refreshProjectData();
                    }}
                    projectId={projectId}
                    onProjectUpdate={refreshProjectData}
                    clearSearchValue={() => {}}
                  />
                </div>
                ) : null}
                <div className="glass-table-wrap">
                <QuoteTable
                  quoteList={quotes}
                  handleDelete={() => {
                    reloadProjectQuotes();
                    refreshProjectData();
                  }}
                  clearSearchValue={() => {}}
                  onProjectUpdate={refreshProjectData}
                />
                </div>
              </>
            )}
          </Tab.Pane>

          <Tab.Pane eventKey="orders">
            {loadingOrders ? (
              <div className="text-center py-5">
                <Spinner animation="border" role="status" />
              </div>
            ) : orders.length === 0 ? (
              <div className="glass-empty">
                <p className="mb-0">No orders associated with this project.</p>
              </div>
            ) : (
              <>
                {!isViewer ? (
                <div className="glass-toolbar mb-3">
                  <OrderModal
                    onSuccessfulSubmit={() => {
                      refreshProjectData();
                    }}
                    projectId={projectId}
                    onProjectUpdate={refreshProjectData}
                    clearSearchValue={() => {}}
                  />
                </div>
                ) : null}
                <div className="glass-table-wrap">
                <OrderTable
                  orderList={orders}
                  handleDelete={() => {
                    setOrders([]);
                    getProjectOrders(token, projectId).then(response => {
                      if (response && response.success) {
                        setOrders(response.orders || []);
                      }
                    });
                    refreshProjectData();
                  }}
                  clearSearchValue={() => {}}
                  onProjectUpdate={refreshProjectData}
                />
                </div>
              </>
            )}
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
      <GlobalOrderContactModal
        open={showGlobalContactModal}
        onClose={() => setShowGlobalContactModal(false)}
        token={token}
        onSettingsUpdated={settings => {
          setGlobalContactSettings(settings);
          setGlobalContactLoadError('');
          showToast('Lab contact saved successfully', 'success', 'top-right', 3000);
        }}
      />
    </GlassWorkspaceShell>
  );
};

export default ProjectDetailPage;

